import { Fragment, useLayoutEffect } from "react";
import styled from "styled-components";
import Footer from "../../components/fragments/Footer";
import NavBar from "../../components/fragments/NavBar";
import HeroBanner from "../../components/fragments/HeroBanner";
import { Container } from "../../components/styles/Layout";

import { CheckCircle, GetApp } from "@material-ui/icons";

import { useStaticSeo } from "../../hooks/useSeo";
import { STATIC_SEO } from "../../data/seo";

const Wrapper = styled.section`
	margin-bottom: 4rem;
	margin-top: 2rem;
`;
const BigTitle = styled.h2`
	position: absolute;
	margin-bottom: 1rem;
	margin-top: 0.2rem;
	z-index: 1;
`;

const Table = styled.div`
	display: grid;
	grid-template-columns: ${(props) => props.sales && "1.5fr 1fr 1fr 1fr"};
	grid-template-columns: ${(props) => props.lettings && "1.5fr 1fr 1fr 1fr 1fr"};
	font-size: 0.8rem;
	font-weight: 400;
`;

const TableTitle = styled.div`
	background: ${(props) => props.grey && "#e8e8e8"};
	padding: 0.5rem 0.75rem;
	border: 1px solid white;
	position: relative;
`;
const TableDesc = styled.div`
	background: ${(props) => props.dark && props.theme.darkblue};
	background: ${(props) => props.light && "#38476F"};
	background: ${(props) => props.grey && "#e8e8e8"};
	color: ${(props) => props.theme.black};
	color: ${(props) => (props.dark || props.light) && "white"};
	text-align: center;
	padding: 0.5rem 0.75rem;
	border: 1px solid white;
`;

const Check = styled(CheckCircle)`
	color: ${(props) => props.theme.green};
	padding: 0;
	margin: -17px auto -9px auto;
`;

const Info = styled.div`
	font-size: 0.8rem;
	margin: 1rem 0.9rem;
	font-style: italic;
`;

const DownloadPDF = styled.div`
	margin: 1rem 0 3rem 0;
	& a {
		font-weight: 500;
		text-transform: uppercase;
		font-size: 0.9rem;
		color: ${(props) => props.theme.darkblue};
		transition: 0.3s;
	}
	& a:hover {
		color: ${(props) => props.theme.green};
		transition: 0.3s;
	}
`;

const DownloadPDFIcon = styled(GetApp)`
	margin: 0 0.5rem -0.4rem 0;
`;

/*
 * Kept in step by hand with front/src/data/guides/lettingsGuideContent.js
 * and salesGuideContent.js's own fee tables - those, not this page, are
 * where the business actually edits pricing day to day (see CreatePostForm
 * for posts; these guide pages are static content, updated by editing the
 * content module directly). If the guides change, this needs updating to
 * match - there is no single shared data source between a plain-HTML
 * content string and this component tree, so drift here is a real risk to
 * watch for, the same way it already happened once (18% vs the guide's
 * 15%, and a "Section 21" row surviving its own abolition).
 *
 * true = a plain checkmark ("Included"); anything else renders as text.
 */
const LETTINGS_TIERS = ["Let Only", "Let & Rent Collection", "Let & Management", "360 Management"];
const LETTINGS_PRICES = ["8.4%", "10.8%", "13.2%", "15%*"];
const LETTINGS_ROWS = [
	["Online Marketing — Rightmove, Zoopla, OnTheMarket", true, true, true, true],
	["To Let Board", true, true, true, true],
	["Professional Photography & Floor Plan", true, true, true, true],
	["Accompanied Viewings", true, true, true, true],
	["Monthly / Annual Statements", true, true, true, true],
	["Utility Transfer", "£78", "£78", true, true],
	["Tenant Referencing & Checks", "£60/tenant", "£60/tenant", true, true],
	["Tenancy Agreement & E-Signatures", "£180", "£180", true, true],
	["Smoke Alarm Check", "£78", "£78", true, true],
	["Bi-Annual Inspections", "£94.80", "£94.80", true, true],
	["Energy Performance Certificate", "£94.80", "£94.80", "£94.80", true],
	["Annual Gas Safety Check", "£94.80", "£94.80", "£94.80", true],
	["Annual Boiler Service", "£94.80", "£94.80", "£94.80", true],
	["Inventory & Check-In", "from £234", "from £234", "from £234", true],
	["End-of-Tenancy Checkout", "£150", "£150", "£150", true],
	["Deposit Registration (DPS)", "£78", "£60", "£30", true],
	["Deposit Dispute Management", "£420", "£300", "£180", true],
	["Additional Visits", "£78", "£78", "£36", true],
	["Notice Serving & Possession Paperwork (Section 8 Grounds)", "£78", "£78", "£78", true],
	// ** because * already marks the 15% introductory rate above; see the note under the table.
	["Handyman Service", "£90/hr", "£90/hr", "£45/hr", "Free with 360**"],
];

/*
 * When the 15% introductory rate on 360 Management ends.
 *
 * A constant rather than a sentence buried in the footnote, so it is
 * greppable and so the next person changing it does not have to find it by
 * reading the page. The previous date sat twenty-six days expired on a live
 * page because nothing anywhere points at it.
 */
const INTRODUCTORY_OFFER_ENDS = "31 December 2026";

const SALES_TIERS = ["Flat Fee Sales", "Standard Sales", "360 Sales"];
const SALES_PRICES = ["£1,200 one-off payment", "1.2% sole / 1.8% multiple", "2.4% sole / 3.6% multiple"];
const SALES_ROWS = [
	["No Obligation Free Property Valuation", true, true, true],
	["Online Marketing — Rightmove, Zoopla, OnTheMarket", true, true, true],
	["Erect For Sale Board", true, true, true],
	["Professional Photography", true, true, true],
	["2D Floor Plan", true, true, true],
	["Registering Buyer Enquiries", true, true, true],
	["Arranging Viewing Appointments", true, true, true],
	["Standard Property Brochure", true, true, true],
	["Confirming Offers & Negotiating", true, true, true],
	["Sales Progression", "£600", true, true],
	["Accompanied Viewings — Monday to Saturday", "from £30", true, true],
	["Accompanied Viewings — Sundays & Bank Holidays", "from £60", "from £60", true],
	["Energy Performance Certificate", "£94.80", "£94.80", true],
	["Premium Listing Upgrade", "from £118.80", "from £118.80", true],
	["Featured Listing Upgrade", "from £234", "from £234", true],
	["Premium Professional Property Brochure", "from £354", "from £354", true],
	["360° Virtual Walkthrough", "from £354", "from £354", true],
	["3D Floor Plan", "from £94.80", "from £94.80", true],
	["Virtual Staging (unfurnished properties)", "from £94.80", "from £94.80", true],
	["Google & Facebook Advertising", "from £354", "from £354", true],
	["Fees Payable", "Upfront — one-off, whether sold or not", "On completion — no sale, no fee", "On completion — no sale, no fee"],
];

function FeeCell(props) {
	const { value, ...rest } = props;
	return <TableDesc {...rest}>{value === true ? <Check /> : value}</TableDesc>;
}

export default function Pricelist() {
    useStaticSeo(STATIC_SEO["/pricelist"]);
	useLayoutEffect(() => {
		document.getElementById("root").scrollTo(0, 0);
	}, []);

	return (
		<>
			<NavBar location="public" pageTitle="Price List" />
			<HeroBanner title="Price List" background="/images/pp-outside-dark.jpg" showRating={false} />
			<Wrapper>
				<Container>
					<Table lettings>
						<TableTitle>
							<BigTitle>Lettings Fees</BigTitle>
						</TableTitle>
						{LETTINGS_TIERS.map((tier, i) => (
							<TableDesc key={tier} dark={i % 2 === 0} light={i % 2 === 1}>
								{tier.toUpperCase()}
							</TableDesc>
						))}

						<TableTitle />
						{LETTINGS_PRICES.map((price, i) => (
							<TableDesc key={price} dark={i % 2 === 0} light={i % 2 === 1}>
								{price}
							</TableDesc>
						))}

						{LETTINGS_ROWS.map(([label, ...values], rowIndex) => (
							<Fragment key={label}>
								<TableTitle grey={rowIndex % 2 === 0}>{label}</TableTitle>
								{values.map((value, colIndex) => (
									<FeeCell key={colIndex} value={value} grey={rowIndex % 2 === 0} />
								))}
							</Fragment>
						))}
					</Table>

					<Info>
						{/*
						  * THIS DATE EXPIRES AND NOTHING WARNS YOU.
						  *
						  * It read "30 September 2026" until 26 October 2026, so for
						  * twenty-six days a live page people reach from search was
						  * advertising a rate against a deadline that had already
						  * passed. Nothing in the build, the deploy or the nightly
						  * jobs checks a date written into copy.
						  *
						  * So: when this one approaches, either move it or drop the
						  * offer and make 15% the plain published rate. Search this
						  * file for INTRODUCTORY_OFFER_ENDS.
						  */}
						All fees include VAT at 20%. *The 360 Management rate of 15% is an introductory offer available until{" "}
						{INTRODUCTORY_OFFER_ENDS}. Rates occasionally change — call 020 8946 7171 to confirm before instructing us.
						<br />
						**Handyman service is free for the first hour, then charged at £30/hr reduced labour cost only, parts &amp;
						materials extra.
					</Info>

					<DownloadPDF>
						<a href="/brochures/Property-People-Lettings-Brochure.pdf" target="_blank" rel="noreferrer">
							<DownloadPDFIcon />
							Download our Lettings brochure
						</a>
					</DownloadPDF>

					<DownloadPDF>
						<a
							href="/legal/Tenant_fees_Assured Shorthold_Tenancies_V3.4.pdf"
							target="_blank"
							rel="noreferrer"
						>
							<DownloadPDFIcon />
							Tenant Fees
						</a>
					</DownloadPDF>

					<Table sales>
						<TableTitle>
							<BigTitle>Sales Fees</BigTitle>
						</TableTitle>
						{SALES_TIERS.map((tier, i) => (
							<TableDesc key={tier} dark={i % 2 === 0} light={i % 2 === 1}>
								{tier.toUpperCase()}
							</TableDesc>
						))}

						<TableTitle />
						{SALES_PRICES.map((price, i) => (
							<TableDesc key={price} dark={i % 2 === 0} light={i % 2 === 1}>
								{price}
							</TableDesc>
						))}

						{SALES_ROWS.map(([label, ...values], rowIndex) => (
							<Fragment key={label}>
								<TableTitle grey={rowIndex % 2 === 0}>{label}</TableTitle>
								{values.map((value, colIndex) => (
									<FeeCell key={colIndex} value={value} grey={rowIndex % 2 === 0} />
								))}
							</Fragment>
						))}
					</Table>

					<Info>All fees include VAT at 20%. Rates occasionally change — call 020 8946 7171 to confirm before instructing us.</Info>

					<DownloadPDF>
						<a href="/brochures/Property-People-Sales-Brochure.pdf" target="_blank" rel="noreferrer">
							<DownloadPDFIcon />
							Download our Sales brochure
						</a>
					</DownloadPDF>
				</Container>
			</Wrapper>
			<Footer />
		</>
	);
}
